import React, { useEffect } from 'react'
import { XIcon } from 'lucide-react'
import { mobile } from './tokens'

interface MobileSheetProps {
  open: boolean
  onClose: () => void
  title?: string
  children: React.ReactNode
  /** Desktop max width */
  maxWidthClass?: string
  footer?: React.ReactNode
}

/**
 * Bottom sheet on phone / centered dialog on wide viewports.
 * Use for pickers, confirms, and secondary flows in the PWA shell.
 */
export function MobileSheet({
  open,
  onClose,
  title,
  children,
  maxWidthClass = 'max-w-lg',
  footer,
}: MobileSheetProps) {
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close"
        onClick={onClose}
      />
      <div
        className={`relative z-10 w-full ${maxWidthClass} bg-white shadow-xl rounded-t-2xl sm:rounded-2xl max-h-[90dvh] flex flex-col mx-0 sm:mx-4`}
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="sm:hidden flex justify-center pt-2 pb-1">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900 truncate">{title || ""}</h3>
          <button
            type="button"
            onClick={onClose}
            className={mobile.ghostBtn}
            aria-label="Close"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 min-h-0 px-4 py-3">{children}</div>
        {footer && (
          <div className="px-4 py-3 border-t border-gray-100 flex gap-2">{footer}</div>
        )}
      </div>
    </div>
  )
}
